import {SanitizeType} from "../../../common/pipes/sanitize.pipe";

/**
 ****************************************************************
 * Interfaces for the scheduling mechanism.
 *
 * Created by Shai Zelman on 16/03/2020.
 ****************************************************************
 */

export interface IPatientAppointment {
	spsID: number;
	orderKey: number;
	requestedProcedureID: number;
	spsCodeKey: number;
	spsCode: string;
	ppsKey: number;
	orderedProcedures: IAppointmentSpsCode[];
	visitKey: number;
	statusKey: number;
	orderingOrganizationKey: number;
	patientID: number;
	requestedByPersonKey: number;
	siteWorklistKey: number;
	patientFirstName: string;
	patientLastName: string;
	examDescription: string;
	startDateTime: Date;
	endDateTime: Date;
	duration: number;
	modalityKey: number;
	facilityLocation: ISelfSchedulingLocation;
	canReschedule: boolean;
	canCancel: boolean;
	canSchedule: boolean;
	canEditForm: boolean; 
	arriveMinBefore: number;
	hashIdentifier: string;
}

export interface IAppointmentSpsCode {
	spsCodeKey: number;
	rpCodeKey: number;
	procedureCodeKey: number
	lateralityKey?: number
}

export interface ISelfSchedulingLocation {
	locationID: number;
	description: string;
	floor: string;
	building: string;
	street: string;
	otherDestination: string;
	city: string;
	province: string;
	postalCode: string;
	phone: string;
	groupID: number;
	groupDescription: string;
	errorMessage: string;
	contactInfo: string;
	orgKeyOfParentShowInRisPortal: number;
	coordinatesLatitude: string;
	coordinatesLongitude: string;
	locationPath: number[];
}

export interface ISelectedStudy {
	spsID?: number;
	spsCodeKey: number;
	requestedProcedureID: number;
	rpCodeKey: number;
	procedureKeys?: ISelfProcedureCodeLaterality[];
	duration?: number;
	durationModifier?: number;
	start?: Date;
	end?: Date;
	modalityKey?: number;
	personReferenceTypeKeys?: number[];
	selectedResourceAllocation?: number[];
	studySeriesKey: number;
	seriesSequenceId?: number;
	isCurrentContextStudy?: boolean;
}

export interface ITimeToClosestAppointment {
	totalDays: number;
	isToday: boolean;
	isTomorrow: boolean;
	days: number;
	weeks: number;
	translationString: string;
	parameters: number[];
}

export interface ITimeToArriveBefore {
	translationString: string;
	parameters: number[];
}

export interface ISearchesPerDay {
	maxResultsPerDay: number;
	maxNumberOfDays: number;
}

export interface IDaysTimeOptions {
	startTime: Date,
	endTime: Date,
	weekdays: boolean[];
}

export enum TimeSlotSearchType {
	RECOMMENDED,
	NORMAL
}

export enum OperatingSystem {
	Android,
	iOS,
	Windows,
	macOS
}

export interface ILocationTreeNode {
	children: ILocationTreeNode[];
	location: ISelfLocation;
}

export interface ISelfLocation {
	orgStructureKey: number;
	description: string;
	coordinatesLatitude?: string;
	coordinatesLongitude?: string;
}

export interface ILightAppointment {
	orderKey: number;
	spsId: number;
	spsCodeKey: number;
}

export interface ILocationSelectionRequest {
	scheduledOrganizationKeys: number[];
	appointments: ILightAppointment[];
	opCodeKey: number;
}

export interface ISearchStudyRequest {
	searchTerm: string;
	languageKey: number;
}

export interface ILocationSelectionResponse {
	locationTreeRoots: ILocationTreeNode[];
	behaviour: LocationSelectionBehaviour;
	locationsForReschedule: ISelfLocation[];
}

export interface ISearchResult {
	SPS_ID: number;
	SPS_Code_Key: number;
	startTime: Date;
	endTime: Date;
	arriveMinBefore: number;
	duration: number;
	modality: number;
	amountOfBookingsInTimeSlot?: number;
	selectedResourceAllocation?: {[key: number]: number};
	portalOrgStructureKey: number;
	portalLocation: string;
	resourceSelection?: IResourceSelection[];
	rpCodeKey?: number;
	procedureCodeKeys?: ISelfProcedureCodeLaterality[];
	hash: string;
	startDateString: string;
}

export interface IScheduleData {
	selectedScheduling: ISearchResult[];
	acquiredDocuments?: IDocumentFile[];
}

export interface IOrderUpdateData {
	acquiredDocuments?: IDocumentFile[];
}

export interface IResourceSelection {
	personKey: number;
	resourceIdKey: number;
	personReferenceTypeKey: number;
}

export enum ResourceSelectionType {
	Auto,
	Manual,
	Group
}

export enum GroupingType {
	NONE = 0,
	MODALITY = 1,
	LOCATION = 2
}

export enum LocationSelectionBehaviour {
	SKIP_SELECTION = 0,
	SELECT_LOCATION = 1,
	NO_LOCATIONS = 2,
}

export interface ISelfModalityTypeGroup {
	modalityTypeGroupKey: number;
	code: string;
	description: string;
	color: string;
}

export interface ISelfProcedureCodeLaterality
{
	codeKey: number;
	lateralityKey?: number;
}

export interface ISpsCode {
	spsCodeKey: number;
	rpCodeKey: number;
	procedureCodesKeysAndLateralities: ISelfProcedureCodeLaterality[];
	code: string;
	formattedDescription: string;
	description: string;
	duration: number;
	preparationTime: number;
}

export interface ICancellationReason {
	statusReasonKey: number;
	name: string;
}

export interface ICancelOrderRequest {
	patientKey: number;
	orderKey: number;
	statusReasonKey?: number;
	comment?: string;
}

export interface IChangeStatusRequest {
	spsId: number;
	statusKey: number; // Target status
	ignoreThresholdTime: boolean;
	statusReasonKey?: number;
	patientKey: number;
	orderKey: number;
	comment?: string;
  rulesValidationOverride: RulesValidationOverride;
}

export interface IRenewReservationRequest {
	sessionId: string;
  minutesUntilExpiration: number;
}

export interface IScheduledAppointment {
	spsID: number;
	spsCodeKey: number;
	startTime: Date;
	contactInfo: ISelfSchedulingLocation;
	arriveMinBefore?: number;
}

export interface IScheduledAppointmentInstruction {
	instructionTitle: string;
	instructionDetails: string[];
}

export interface IScheduleResponseDetails
{
	instructions: string;
	location: ISelfSchedulingLocation;
	modalityDescription: string;
	spsCodeKey: number;
	orderKey: number;
}

export interface IPortalScheduleChangeMessage {
	sessionId: string;
	scheduleChanges: IScheduleChangeData[]
}

export interface IScheduleChangeData {
	day: Date;
	modality: number;
}

export interface IReservation {
	sessionId: string;
  minutesUntilExpiration: number;
	timeslots: IReservationTimeslot[];
}

export interface IReservationTimeslot {
	spsId: number;
	spsCodeKey: number;
	modality: number;
	startTime: Date;
	duration: number;
	personKeys: number[];
}

export interface IOrgInstructionRequest {
	orgStructureKey: number;
	languageKey: number;
}

export interface ISPSInstructionRequest {
	languageKey: number;
	spsCodeKey: number;
}

export interface ICheckCanNewOrderAndScheduleRequest {
	spsCodeKey: number;
}

export interface IBreadcrumb {
	title: string;
	url: string;
	stepNumber?: number;
}

export interface IDocumentFile {
	content: string;
	docExtension: string;
	name: string;
	size: number;
	validationState: DocumentValidationState;
	scanDocTypeKey: number;
}

export interface IDocumentElement {
	index: number;
	name: string;
	size: number;
	isPdf: boolean;
	validationState: DocumentValidationState;
	scanDocTypeKey: number;
}

export enum ScheduleOpCode {
	Reschedule,
	OrderSchedule,
	NewOrderSchedule,
	EditForm
}

export enum ReservationResult  {
	ReservationFailed,
	ReservationObtained,
	TimeslotTaken
}

export enum DocumentValidationState {
	ValidFile,
	FileTooBig,
	IncorrectFileType,
}

// Rules validation

export enum RulesValidationOverride {
  Default,
  NoOverride,
  Override
}

export enum ClinicalRuleSeverity { Warning, Error }

export enum ClinicalRuleSource { RuleSet, RulesValidation, ValidationException, UnexpectedException }

export interface IClinicalRule {
  severity:ClinicalRuleSeverity;
  source:ClinicalRuleSource;
  message:string;
  messageParameters?:{};
  overrideParameter?:string;
  exception?:string;
  translationNeeded:boolean;
}

export enum PatientDataLevel {
	Patient,
	Order
};
